import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEdit, FaTrash, FaCheckSquare, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';
import { updateProductPrice } from '../services/apiService';

// Image dimensions configuration
const IMAGE_DIMENSIONS = {
  thumbnail: { width: 300, height: 300 },
  medium: { width: 600, height: 600 },
  large: { width: 900, height: 900 }
};

// Optimized image component with loading states
const OptimizedImage = ({ src, alt, className, isPriority = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <picture>
        <img
          src={src}
          alt={alt}
          width={IMAGE_DIMENSIONS.medium.width}
          height={IMAGE_DIMENSIONS.medium.height}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          loading={isPriority ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setError(true);
          }}
        />
      </picture>
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-500">Image not available</span>
        </div>
      )}
    </div>
  );
};

/**
 * Inline price editor.
 *
 * Replaces the legacy edit-price modal for Galexis articles. Two states:
 *   1. Idle: a labeled button — "Add Price" (FaPlus, amber) for an
 *      article that has no usable price yet, or "Update Price" (FaEdit,
 *      blue) for one that does.
 *   2. Editing: an inline input row with Save/Cancel + per-row error.
 *
 * Why inline instead of modal:
 *   • The admin opens this dozens of times in a row when rescuing the
 *     unpriced bucket; a modal trip per click is friction.
 *   • Errors stay next to the input that caused them — no more
 *     "nothing happened" because the global error banner was hidden
 *     behind the modal backdrop.
 *   • Optimistic local-state update via `onSaved` removes the just-
 *     priced item from the rescue list instantly, no re-fetch needed.
 *
 * Keyboard: Enter saves, Escape cancels. autoFocus on open.
 */
const PriceEditor = ({ article, onSaved }) => {
  const { t } = useLanguage();

  const currentPrice =
    article?.price?.currentPrice ??
    article?.adminRetailPrice ??
    article?.basePrice ??
    null;
  const hasPrice =
    currentPrice != null &&
    Number.isFinite(Number(currentPrice)) &&
    Number(currentPrice) > 0;

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const open = (e) => {
    // Card has an outer onClick that navigates to the product detail
    // page; stop propagation so clicking the button doesn't both edit
    // AND navigate away.
    e?.stopPropagation();
    setValue(hasPrice ? String(currentPrice) : '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue('');
    setError(null);
  };

  const save = async () => {
    const price = parseFloat(value);
    if (!Number.isFinite(price) || price <= 0) {
      setError(t('products.errors.enterValidPrice'));
      return;
    }
    const pc = Number(article?.pharmacode);
    if (!Number.isFinite(pc)) {
      setError(`Invalid pharmacode: ${JSON.stringify(article?.pharmacode)}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await updateProductPrice(pc, price);
      if (r?.success) {
        onSaved?.({
          pharmacode: pc,
          basePrice: r.data?.basePrice ?? price,
          adminRetailPrice: r.data?.adminRetailPrice ?? price,
        });
        setEditing(false);
        setValue('');
      } else {
        setError(r?.error || t('products.errors.updatePrice'));
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message;
      setError(
        msg ? `${msg} (HTTP ${e?.response?.status ?? '?'})` : t('products.errors.updatePrice')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={open}
        className={`flex items-center gap-1 text-sm font-medium ${
          hasPrice
            ? 'text-blue-500 hover:text-blue-700'
            : 'text-amber-600 hover:text-amber-700'
        }`}
      >
        {hasPrice ? <FaEdit /> : <FaPlus />}{' '}
        {hasPrice
          ? t('products.productCard.updatePrice')
          : t('products.productCard.addPrice')}
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-1 w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        <input
          type="number"
          step="0.01"
          min="0"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            else if (e.key === 'Escape') cancel();
          }}
          disabled={saving}
          placeholder="CHF"
          className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
        />
        <button
          onClick={save}
          disabled={saving}
          className={`px-2 py-1 text-xs font-medium rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {saving ? '…' : t('products.modals.editPrice.update')}
        </button>
        <button
          onClick={cancel}
          disabled={saving}
          className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
        >
          {t('products.modals.editPrice.cancel')}
        </button>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
};

// Product Card Component - Updated to match website styling
const ProductCard = ({
  product,
  onEdit,
  onDelete,
  onSelect,
  onPriceSaved,
  isSelected = false,
  isSelectionMode = false,
  showActions = true
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Process product data to match website structure
  let article;

  // Handle products with nested articles (like generic products)
  if (product.articles && Array.isArray(product.articles)) {
    const firstArticle = product.articles[0];
    // Handle images - can be array directly (search results) or object with F/M property
    let imagesArray = [];
    if (Array.isArray(firstArticle.images)) {
      imagesArray = firstArticle.images;
    } else if (Array.isArray(firstArticle.images?.F)) {
      imagesArray = firstArticle.images.F;
    } else if (Array.isArray(firstArticle.images?.M)) {
      imagesArray = firstArticle.images.M;
    }

    article = {
      pharmacode: firstArticle.pharmacode,
      description: {
        description: firstArticle.description?.description || 'No description available',
        longDescription: firstArticle.description?.longDescription || 'No description available'
      },
      hasPhoto: firstArticle.hasPhoto || false,
      images: {
        F: imagesArray.map(img => (typeof img === 'string' ? img.replace(/^uploads[\\/]/, '') : img))
      },
      price: firstArticle.price || firstArticle.prices || firstArticle.priceInfo || null
    };
  } else {
    // Handle regular products (direct product structure) - including search results
    const description = product.description;

    // Handle different description formats
    let descriptionText = 'No description available';
    let longDescriptionText = 'No long description available';

    if (typeof description === 'string') {
      descriptionText = description;
      longDescriptionText = description;
    } else if (description && typeof description === 'object') {
      descriptionText = description.description || description.longDescription || 'No description available';
      longDescriptionText = description.longDescription || description.description || 'No long description available';
    }

    article = {
      pharmacode: product.pharmacode || product.productNumber || product._id,
      description: {
        description: descriptionText,
        longDescription: longDescriptionText
      },
      hasPhoto: product.hasPhoto || false,
      images: {
        F: (() => {
          let arr = [];
          if (Array.isArray(product.images)) arr = product.images;
          else if (Array.isArray(product.images?.F)) arr = product.images.F;
          else if (Array.isArray(product.images?.M)) arr = product.images.M;
          return arr.map(img => (typeof img === 'string' ? img.replace(/^uploads[\\/]/, '') : img));
        })()
      },
      price: product.prices || product.priceInfo || null
    };
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`group relative border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200 ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}
      // Only allow navigation if not in selection mode
      onClick={(e) => {
        if (!isSelectionMode && !e.target.closest('button')) {
          const productIdForRoute = article?.pharmacode;
          if (productIdForRoute) {
            navigate(`/products/${encodeURIComponent(String(productIdForRoute))}`);
          }
        }
      }}
      style={{ cursor: !isSelectionMode ? 'pointer' : 'default' }}
    >
      {/* Selection Checkbox - Only show in selection mode */}
      {isSelectionMode && (
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={() => onSelect && onSelect(article)}
            className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
              isSelected
                ? 'bg-indigo-500 border-indigo-500 text-white shadow-sm'
                : 'bg-white/95 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
            }`}
          >
            {isSelected && <FaCheckSquare className="w-3 h-3" />}
          </button>
        </div>
      )}

      {/* Fixed height container */}
      <div className="aspect-[4/3] w-full relative">
        {/* Image wrapper */}
        <div className="absolute inset-0 p-3">
          {(() => {
            const imageUrl = getArticleImageUrl(article, 'F');
            if (!imageUrl) {
              return (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  <span>{t('products.productCard.noImage')}</span>
                </div>
              );
            }

            return (
              <OptimizedImage
                src={imageUrl}
                alt={article.description?.longDescription || 'Product Image'}
                className="w-full h-full object-contain"
                isPriority={false}
              />
            );
          })()}
        </div>
      </div>

      {/* Content section */}
      <div className="p-3 flex flex-col min-h-[120px]">
        {/* Title and description */}
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 line-clamp-2">
            {t('products.productCard.pharmacode')}: {article.pharmacode}
          </h3>
          <p className="mt-1 text-sm italic text-gray-900 font-medium line-clamp-2">
            {article.description?.description || t('products.productCard.noDescription')}
          </p>
          <p className="text-sm text-gray-900 font-medium mt-1 line-clamp-2">
            {article.description?.longDescription || t('products.productCard.noLongDescription')}
          </p>
        </div>

        {/* Price */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          <PriceDisplay article={article} />
        </div>

        {/* Actions
            - Manual ("from scratch") products: full edit flow → opens the
              product form modal (catalog data IS editable).
            - Galexis products: catalog data is read-only by policy;
              price is the only mutable field, so we use the inline
              PriceEditor instead of a modal. */}
        {showActions && (
          <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-100 mt-2">
            {product.source === 'manual' ? (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(article); }}
                className="text-blue-500 hover:text-blue-700 flex items-center gap-1 text-sm"
              >
                <FaEdit /> {t('products.editProduct')}
              </button>
            ) : (
              <PriceEditor article={article} onSaved={onPriceSaved} />
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(article); }}
              className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
            >
              <FaTrash /> {t('products.productCard.delete')}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;
